// adapters.ts
import type { Agenda, Session, Settings, HeaderInfo, ISO8601 } from "./types";

/** Coerce “epoch seconds” from number/ms/string; returns undefined if invalid. */
function toEpochSec(x: unknown): number | undefined {
  if (typeof x === "number" && isFinite(x)) return x < 1e12 ? x : Math.floor(x / 1000);
  if (typeof x === "string" && /^\d+$/.test(x)) {
    const n = Number(x);
    return n < 1e12 ? n : Math.floor(n / 1000);
  }
  return undefined;
}

/** String or undefined */
const toStr = (x: unknown) => (typeof x === "string" ? x : undefined);
/** Int or undefined */
function toInt(x: unknown): number | undefined {
  const n = typeof x === "number" ? x : typeof x === "string" && /^\d+$/.test(x) ? Number(x) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** Normalize one session to your Session shape. Preserves extra fields. */
export function toSession(raw: any): Session {
  const sessioninc = toInt(raw?.sessioninc) ?? 0;
  const sessiontitle = toStr(raw?.sessiontitle);
  const time_start = toEpochSec(raw?.time_start ?? raw?.start ?? raw?.ts_start);
  const time_end   = toEpochSec(raw?.time_end   ?? raw?.end   ?? raw?.ts_end);

  // Put normalized fields last so they override raw if conflicts exist
  return { 
    ...raw,
    sessioninc,
    sessiontitle,
    time_start,
    time_end,
  };
}

/** Normalize agenda. Keeps appearances/files when present. Optionally sorts sessions. */
export function toAgenda(a: any, opts?: { sort?: boolean }): Agenda {
  const sessions = Array.isArray(a?.sessions) ? a.sessions.map(toSession) : [];
  if (opts?.sort) sessions.sort((A: Session, B: Session) => (A.time_start ?? 0) - (B.time_start ?? 0));
  return {
    sessions,
    appearances: Array.isArray(a?.appearances) ? a.appearances : [],
    files: Array.isArray(a?.files) ? a.files : [],
    // extra API fields are allowed by your Agenda index signature
    requestTime: a?.requestTime ? a.requestTime : '',
  };
}

/** Pass-through settings normalizer; firm up known sub-objects. */
// export function toSettings(s: any): Settings {
//   const background = s?.background && typeof s.background === "object"
//     ? { url: String(s.background.url ?? ""), version: toStr(s.background.version) }
//     : undefined;

//   return {
//     ...s,
//     background,
//     skin: toStr(s?.skin) ?? null,
//     locale: toStr(s?.locale),
//     timeZone: toStr(s?.timeZone),
//   };
// }

type BackgroundCfg = { url: string; timeStamp?: number };

export function toSettings(s: any): Settings {
  const files: any[] | undefined = Array.isArray(s?.files) ? s.files : undefined;

  let background: BackgroundCfg | undefined;

  // 1) Prefer explicit background if present (supports signedUrl or url here)
  const bg0 = s?.background;
  if (typeof bg0 === "string") {
    const u = toStr(bg0);
    if (u) background = { url: u };
  } else if (bg0 && typeof bg0 === "object") {
    const u = toStr((bg0 as any).signedUrl ?? (bg0 as any).url);
    if (u) background = { url: u, timeStamp: toInt((bg0 as any).timeStamp) };
  }

  // 2) Otherwise, derive from files (type === "background" preferred; pick newest by timeStamp)
  if (!background && files?.length) {
    const bgs = files
      .filter(f => f?.type === "background" && typeof f.signedUrl === "string" && f.signedUrl)
      .sort((a, b) => (toInt(b.timeStamp) ?? -Infinity) - (toInt(a.timeStamp) ?? -Infinity));

    const pick =
      bgs[0] ??
      files.find(f => typeof f?.signedUrl === "string" && f.signedUrl);

    if (pick) {
      background = { url: String(pick.signedUrl), timeStamp: toInt(pick.timeStamp) };
    }
  }

  return {
    ...s,
    background,                        // { url, timeStamp? } | undefined
    skin: toStr(s?.skin) ?? null,
    locale: toStr(s?.locale),
    timeZone: toStr(s?.timeZone),
  };
}

/** Extract optional header bits from your payload (top-level only). */
export function toHeader(payload: any): HeaderInfo {
  return {
    agendaTitle: toStr(payload?.agendaTitle),
    label: toStr(payload?.label),
    timestamp: toStr(payload?.timestamp), // leave undefined if your API doesn't send it
  };
}

export function toEpochSeconds(iso: ISO8601 | undefined): number | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);            // parses ISO; returns NaN on bad input
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}
